import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import AnnotationCanvas from '../annotation/AnnotationCanvas';
import { validateEntries } from '../../utils/annotationGeometry';
import { trapTab } from '../../utils/focusTrap';

/*
 * "Outline the tissue" in Field Mode's capture summary (docs/GAME_DESIGN.md
 * Part III section 18, PR10): on one of the specimen's photographs, paint the
 * cap, the stipe and the gills or pores with the annotation workbench's own
 * canvas (the wand fills a region; whatever is left unpainted is background).
 *
 * Saved with the batch on the phone (`outline`); the outbox sends it with
 * PUT /photos/{id}/annotations once that photo is on the server, so nothing
 * here needs a connection. In a live field event a complete outline (cap,
 * stipe and fertile layer) earns the card a bonus. It feeds the training
 * masks exactly like the workbench's (backend/ai/datasets/annotation_masks.py).
 *
 * A dialog of its own inside the summary: the summary leaves keys pressed
 * inside another dialog alone (FieldCaptureSummary's keydown handler).
 */
export default function FieldOutline({ photos, urls, initial, onSave, onClose }) {
  const { t } = useTranslation('field');
  const box = useRef(null);
  const heading = useRef(null);
  const choosable = photos.filter((p) => urls[p.id]);
  const preferred = choosable.find((p) => p.id === initial?.photoId) || choosable.find((p) => p.view === 'cap_top')
    || choosable[0];
  const [photoId, setPhotoId] = useState(preferred?.id || null);
  const [entries, setEntries] = useState(initial?.photoId === preferred?.id ? initial?.entries || [] : []);
  const [size, setSize] = useState(null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    const before = document.activeElement;
    heading.current?.focus();
    return () => { before?.focus?.(); };
  }, []);

  const save = () => {
    // What the server takes, nothing the canvas keeps for itself.
    const clean = entries.map(({ specimen_index: specimenIndex, layer, fertile_type: fertileType, geometry }) => (
      { specimen_index: specimenIndex, layer, fertile_type: fertileType ?? null, geometry }));
    if (validateEntries(clean)) {
      setProblem(t('outline.invalid'));
      return;
    }
    onSave({ photoId, entries: clean, width: size?.width || null, height: size?.height || null });
  };

  return createPortal(
    <div className="hub-drawer-backdrop" style={os.backdrop} data-testid="field-outline-backdrop">
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby="field-outline-title" style={os.box}
        data-testid="field-outline"
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
          trapTab(box.current, e);
        }}>
        <h2 id="field-outline-title" ref={heading} tabIndex={-1} style={{ marginTop: 0, fontSize: 18 }}>
          {t('outline.title')}
        </h2>
        <p style={{ marginTop: 0 }}>{t('outline.help')}</p>
        {choosable.length > 1 && (
          <div role="group" aria-label={t('outline.photo')} style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {choosable.map((p, i) => (
              <button key={p.id} type="button" aria-pressed={p.id === photoId} data-testid={`field-outline-photo-${i}`}
                style={{ ...os.pick, outline: p.id === photoId ? '3px solid var(--c-primary)' : 'none' }}
                onClick={() => { setPhotoId(p.id); setEntries([]); }}>
                <img src={urls[p.id]} alt={t(`views.${p.view}`)} style={{ width: 48, height: 48, objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        )}
        <div style={{ height: '55vh', minHeight: 260 }}>
          {photoId && (
            <AnnotationCanvas key={photoId} src={urls[photoId]} value={entries} onChange={setEntries}
              onImageSize={setSize} />
          )}
        </div>
        {problem && <p role="alert" style={{ color: 'var(--c-danger, #c62828)' }}>{problem}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button type="button" style={os.secondary} onClick={onClose} data-testid="field-outline-cancel">
            {t('outline.cancel')}
          </button>
          <button type="button" style={os.primary} onClick={save} disabled={!entries.length}
            data-testid="field-outline-save">{t('outline.save')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const os = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 3300, display: 'grid',
    placeItems: 'center', padding: 12 },
  box: { background: 'var(--c-surface, #fff)', color: 'var(--c-text-fg, #111)', borderRadius: 14, padding: 16,
    width: 'min(760px, 100%)', maxHeight: '96dvh', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,.4)' },
  pick: { padding: 0, border: 'none', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', background: 'none' },
  primary: { minHeight: 44, padding: '0 16px', borderRadius: 8, border: 'none', background: 'var(--c-primary, #2e7d32)',
    color: '#fff', font: 'inherit', fontWeight: 600, cursor: 'pointer' },
  secondary: { minHeight: 44, padding: '0 16px', borderRadius: 8, border: '1px solid var(--c-border, #ccc)',
    background: 'transparent', font: 'inherit', cursor: 'pointer' },
};
